import { useNavigate } from "react-router-dom"
import { DataTable } from "../DataTableAwayter/DataTableAwayter.component"
import { Machine } from "../../utils/models/Machine"
import { ColumnFiltersState, createColumnHelper, SortingState } from "@tanstack/react-table"
import { useTranslation } from "react-i18next"
import { formatNumberWithLeadingZeros } from "aw-generic-functions"
import { Instalation } from "../../utils/models/Instalation"
import { IPosition } from "aw-map"
import "./MachineDashboardTable.scss"
import { useEffect, useState } from "react"
import { getAllMachines } from "../../utils/services/machine.service"
import { useSelector } from "react-redux"
import { selectUser } from "../../utils/redux/userSlice"
import { constants } from "buffer"
import { DataGrid } from "aw-data-grid"

type MachinesDashboardProps = {
    machines: Machine[]
    manual: boolean
    filtres: any
    setFilters: any
    instalations: Instalation[]
    mapPoints: IPosition[]
    globalSearch?: any;
    setGlobalSearch?:any;
}

const MachinesDashboard = ({ machines, manual, instalations, mapPoints, globalSearch, setGlobalSearch, filtres, setFilters}: MachinesDashboardProps) => {
    const [data, setData] = useState<Machine[]>([])
    const user = useSelector(selectUser)

    const getStableRandomIndex = (id: number, max: number): number => {
        const hashed = (id * 0x9e3779b1) >>> 0 
        return hashed % max
    }
    const getMachineColor = (rowData: Machine) => {
        const instid = mapPoints.find((p) => p.name === rowData.instalation.name)?.id
        const imachines = instalations.find((i) => i.idinstalation === instid)?.machines
        const index = imachines?.findIndex((p) => p.idmachine === rowData.idmachine) || 0
        const instcolor = mapPoints.find((p) => p.name === rowData.instalation.name)?.iconColor ?? "#7E7E7E"
        const disabledColors = ["#7e7e7e", "#008000"]
        const allColors = ["#FF0000", "#7E7E7E", "#008000"]
        return index === 0
            ? instcolor
            : instcolor === "#008000"
            ? "#008000"
            : instcolor === "#7E7E7E"
            ? disabledColors[getStableRandomIndex(rowData.idmachine, 2)]
            : allColors[getStableRandomIndex(rowData.idmachine, 3)]
    }

    const getAllMachinesAux = () => {
        //setLoading(true);
        getAllMachines(user.token)
            .then((json: Machine[]) => {
                setData(json)
            })
            .catch(() => {
                //showToastTimeOut(t('ErrorLoad'), true);
            })
            .finally(() => {
                setTimeout(() => {
                    //setLoading(false);
                }, 500)
            })
    }

    useEffect(() => {
        getAllMachinesAux()
    }, [manual, user.token])

    const navigate = useNavigate()
    const { t } = useTranslation()
    const columnHelper = createColumnHelper<Machine>()
    const stateOrder = [t("Connected"), t("Disconnected"), t("Alarms")]
    const [sorting, setSorting] = useState<SortingState>([])
    const columns = [
        columnHelper.accessor("idmachine", {
            header: t("Identifier"),
            enableSorting: true,
            enableColumnFilter: false,
            cell: (info) => formatNumberWithLeadingZeros(info.getValue(), 5)
        }),
        columnHelper.accessor("name", {
            header: t("Name"),
            enableSorting: true,

            cell: (info) => info.getValue()
        }),
        columnHelper.accessor("instalation.name", {
            id: "instalation",
            header: t("Installation"),
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filterOptions: Array.from(
                    new Set(instalations.map((i) => i.name))
                ).map((name) => ({ label: name, value: name }))
            },
            cell: (info) => {
                return (
                    <div
                        className='instalation-button'
                        onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/userInstalations/${info.row.original.instalation.idinstalation}`)
                        }}
                    >
                        {info.getValue()}
                    </div>
                )
            }
        }),
        columnHelper.accessor("serial_number", {
            header: t("NSerie"),
            enableSorting: true,
            cell: (info) => info.getValue()
        }),
        columnHelper.accessor("v_firmware_hmi", {
            header: "V. Firmware HMI",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (info) => info.getValue()
        }),
        columnHelper.accessor(
            (row) => {
                const color = getMachineColor(row)
                // Map color to state string
                if (color === "#008000") return stateOrder[0] // Running
                if (color === "#7E7E7E") return stateOrder[1] // Stopped
                if (color === "#FF0000") return stateOrder[2] // Unknown
                return "unknown"
            },
            {
                id: "state",
                header: t("State"),
                enableSorting: true,
                enableColumnFilter: true,
                filterFn: (row, columnId, filterValue: string[]) => {
                    const val = row.getValue<string>(columnId)
                    return filterValue.length === 0 || filterValue.includes(val)
                },
                sortingFn: (a, b, columnId) =>
                    stateOrder.indexOf(a.getValue(columnId)) - stateOrder.indexOf(b.getValue(columnId)),
                meta: {
                    filterOptions: stateOrder.map((elem) => ({ label: elem, value: elem }))
                },
                cell: ({ row }) => {
                    const color = getMachineColor(row.original)
                    return (
                        <div
                            style={{
                                width: "1rem",
                                height: "1rem",
                                borderRadius: "50%",
                                backgroundColor: color
                            }}
                        />
                    )
                }
            }
        )
    ]
    return (
        <div className='p-2 pt-0 pb-0'>
            <div
                className='m-1 p-2 pb-2 mt-0'
                style={{ backgroundColor: "var(--background-secondary)", borderRadius: "4px" }}
            >
                <DataGrid
                    data={machines}
                    onRowClick={(row) => navigate(`/userMachines/${row.idmachine}`)}
                    columns={columns}
                    manual={false}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    globalFilter={globalSearch}
                    onGlobalFilterChange={setGlobalSearch}
                    columnFilters={filtres} 
                    onColumnFiltersChange={setFilters} 
                    filtersPlaceholder={t('Search')}
                />
            </div>
        </div>
    )
}

export default MachinesDashboard
